import {t} from '@lingui/macro';
import {chain} from 'lodash';

import {parseRecipient} from '../../../../helpers/models/recipientHelpers';
import {buildVisualRecipientForEmail} from '../../../../helpers/models/recipientParsers';
import {ContactSourcesEnum} from '../../../../models/api/contactModel';
import {RecipientRolesEnum} from '../../../../models/api/recipientModel';
import {VisualRecipientModel} from '../../../../models/api/visualRecipientModel';
import {ThunkAction} from '../../../appThunk';
import {getHost} from '../../../data/dataSelectors';
import {composeMessageAction} from '../../../data/messages/draftMessagesActions';
import {getFlatGlobalAdmins} from '../../../data/teammates/teammatesSelectors';
import {getLocalize, Localizer} from '../../../stateless/internationalizationSelectors';

/*
 * Constants.
 */

const salesEmailAddress = 'sales@frontapp.com';
const baseUpgradePlanMessage = {
  subject: t('trial-ended-upgradeemail-subject')`Front trial expired`,
  text: t('trial-ended-upgradeemail-text')`Our Front trial has expired`,
};

/*
 * Action creators.
 */

export function composeUpgradePlanMessageToAdminAction(): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const localize = getLocalize(state);
    const globalAdminRecipients = chain(getFlatGlobalAdmins(state))
      .map((teammate) => parseRecipient(ContactSourcesEnum.EMAIL, RecipientRolesEnum.TO, teammate.email))
      .compact()
      .value();

    const localizedBaseMessage = createLocalizedBaseMessage(localize);
    const recipientsAndHtml =
      globalAdminRecipients.length === 0
        ? createSalesEmail(localize)
        : createAdminEmail(globalAdminRecipients, getHost(state), localize);

    return dispatch(composeMessageAction({...localizedBaseMessage, ...recipientsAndHtml}));
  };
}

/*
 * Helpers.
 */

function createSalesEmail(localize: Localizer) {
  const salesRecipient = buildVisualRecipientForEmail(RecipientRolesEnum.TO, salesEmailAddress);
  const html = localize(t('trial-ended-upgradeemail-tosales')`
    Hi,
    <br/>
    <br/>
    Our Front trial has expired — could we discuss options for upgrading?
  `);

  return {recipients: [salesRecipient], html};
}

function createAdminEmail(recipients: VisualRecipientModel[], host: string, localize: Localizer) {
  const billingLink = `${host}/settings/billing`;
  const html = localize(t('trial-ended-upgradeemail-toadmin')`
    Hi,
    <br/>
    <br/>
    Our Front trial has expired — could you <a href=${billingLink}>upgrade our plan</a>
    or contact ${salesEmailAddress} so we can continue to use it?
  `);

  return {recipients, html};
}

function createLocalizedBaseMessage(localize: Localizer) {
  return {
    subject: localize(baseUpgradePlanMessage.subject),
    text: localize(baseUpgradePlanMessage.text),
  };
}
